import { useEffect, useMemo, useRef, useState } from 'react'
import { Plus, Send, Pencil, Trash2, Sparkles, Clock4, LayoutList, Users, UserCheck, UserX, UserPlus, ChevronDown, Check, Search } from 'lucide-react'
import { Modal, ConfirmModal } from '../../common/Modal/Modal'
import Select, { Pagination } from '../../common/Select/Select'

/* ═══════════════════════════════════════════════════════════
   API
═══════════════════════════════════════════════════════════ */
const API_BASE = 'https://supadaa.com/api'

function getToken() {
  const keys = ['token', 'admin_token', 'adminToken', 'authToken', 'auth_token', 'access_token', 'accessToken']
  for (const k of keys) {
    const v = localStorage.getItem(k) || sessionStorage.getItem(k)
    if (v) return v.replace(/^"|"$/g, '').replace(/^Bearer\s+/i, '')
  }
  for (let i = 0; i < localStorage.length; i++) {
    try {
      const o = JSON.parse(localStorage.getItem(localStorage.key(i)))
      if (o && typeof o === 'object' && o.token) return o.token
    } catch (e) { /* json nathi */ }
  }
  return ''
}

async function api(path, body) {
  const token = getToken()
  const res = await fetch(API_BASE + path, {
    method: body === undefined ? 'GET' : 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  })
  const json = await res.json().catch(() => ({}))
  if (!res.ok || json.status === false) {
    const first = json.errors ? Object.values(json.errors)[0]?.[0] : ''
    throw new Error(json.message || first || 'Request failed')
  }
  return json
}

/* ═══════════════════════════════════════════════════════════
   CONFIG
═══════════════════════════════════════════════════════════ */
const STATUS = {
  sent: { cls: 'badge-active', l: 'Sent' },
  scheduled: { cls: 'badge-pending', l: 'Scheduled' },
  draft: { cls: 'badge-warning', l: 'Draft' },
}

const AUDIENCES = [
  { value: 'All Users', desc: 'Every registered customer', icon: Users },
  { value: 'Verified Users', desc: 'Email verified customers', icon: UserCheck },
  { value: 'Unverified Users', desc: 'Email not verified yet', icon: UserX },
  { value: 'New Users', desc: 'Joined in the last 30 days', icon: UserPlus },
]

// badha audience nu reach server automatic gane chhe
const AUTO_REACH = AUDIENCES.map(a => a.value)

// table upar na filter tabs
const FILTER_TABS = [
  { key: 'all', label: 'All' },
  { key: 'sent', label: 'Sent' },
  { key: 'scheduled', label: 'Scheduled' },
  { key: 'draft', label: 'Draft' },
]

const STATUS_OPTIONS = [
  { value: 'sent', label: 'Send now' },
  { value: 'scheduled', label: 'Schedule' },
  { value: 'draft', label: 'Draft' },
]

const blankForm = () => ({
  title: '', audience: 'All Users', message: '', status: 'sent', reach: '', scheduled_at: '',
})

const pct = (n, total) => (total ? Math.round((n / total) * 100) : 0)

/* ═══════════════════════════════════════════════════════════
   AUDIENCE SELECT (custom dropdown)
═══════════════════════════════════════════════════════════ */
function AudienceSelect({ value, onChange }) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)
  const current = AUDIENCES.find(a => a.value === value) || AUDIENCES[0]

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
    }
  }, [])

  return (
    <div className="aud-select" ref={ref}>
      <button
        type="button"
        className={`aud-trigger${open ? ' open' : ''}`}
        onClick={() => setOpen(o => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="aud-ico"><current.icon size={16} /></span>
        <span className="aud-txt">
          <b>{current.value}</b>
          <small>{current.desc}</small>
        </span>
        <ChevronDown size={16} className="aud-chev" />
      </button>

      {open && (
        <ul className="aud-menu" role="listbox">
          {AUDIENCES.map(a => (
            <li key={a.value} role="option" aria-selected={a.value === value}>
              <button
                type="button"
                className={`aud-opt${a.value === value ? ' active' : ''}`}
                onClick={() => { onChange(a.value); setOpen(false) }}
              >
                <span className="aud-ico"><a.icon size={16} /></span>
                <span className="aud-txt">
                  <b>{a.value}</b>
                  <small>{a.desc}</small>
                </span>
                {a.value === value && <Check size={16} className="aud-tick" />}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

/* ═══════════════════════════════════════════════════════════
   PAGE
═══════════════════════════════════════════════════════════ */
export default function PushNotification() {
  const [notifs, setNotifs] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState('')
  const [saving, setSaving] = useState(false)
  const [formError, setFormError] = useState('')
  const [modalOpen, setModalOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [active, setActive] = useState(null)
  const [form, setForm] = useState(blankForm())
  const [statusFilter, setStatusFilter] = useState('all')
  const [query, setQuery] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)

  /* ---------- load ---------- */
  const load = async () => {
    setLoading(true)
    setLoadError('')
    try {
      const res = await api('/push-notifications')
      setNotifs(res.data || [])
    } catch (e) {
      setLoadError(e.message)
    } finally {
      setLoading(false)
    }
  }
  useEffect(() => { load() }, [])

  const openAdd = () => {
    setActive(null)
    setForm(blankForm())
    setFormError('')
    setModalOpen(true)
  }

  const openEdit = (n) => {
    setActive(n)
    const f = { ...blankForm(), ...n.raw }
    if (!AUDIENCES.some(a => a.value === f.audience)) f.audience = 'All Users'
    setForm(f)
    setFormError('')
    setModalOpen(true)
  }

  /* ---------- save (statusOverride = 'draft' for "Save as Draft") ---------- */
  const save = async (statusOverride) => {
    const status = statusOverride || form.status

    if (!form.title.trim()) {
      setFormError('Notification title lakho.')
      return
    }
    if (status !== 'draft' && !form.message.trim()) {
      setFormError('Message body lakho (draft sivay jaruri chhe).')
      return
    }
    if (status === 'scheduled' && !form.scheduled_at) {
      setFormError('Scheduled mate date & time select karo.')
      return
    }

    setSaving(true)
    setFormError('')
    try {
      const payload = {
        ...(active ? { id: active.id } : {}),
        title: form.title.trim(),
        audience: form.audience,
        message: form.message.trim(),
        status,
        reach: form.reach,
        scheduled_at: status === 'scheduled' ? form.scheduled_at : null,
      }
      const res = await api(active ? '/push-notifications/update' : '/push-notifications/create', payload)
      const row = res.data
      setNotifs(p => (active ? p.map(n => (n.id === active.id ? row : n)) : [row, ...p]))
      setModalOpen(false)
    } catch (e) {
      setFormError(e.message)
    } finally {
      setSaving(false)
    }
  }

  const onDelete = async () => {
    try {
      await api('/push-notifications/delete', { id: active.id })
      setNotifs(p => p.filter(n => n.id !== active.id))
    } catch (e) {
      alert(e.message)
    }
    setDeleteOpen(false)
    setActive(null)
  }

  /* ---------- stats ---------- */
  const total = notifs.length
  const sentCount = notifs.filter(n => n.status === 'sent').length
  const scheduledCount = notifs.filter(n => n.status === 'scheduled').length
  const draftCount = notifs.filter(n => n.status === 'draft').length

  const tabCounts = { all: total, sent: sentCount, scheduled: scheduledCount, draft: draftCount }

  // status tab + search (title, audience, ID, message) banne ek sathe lagu thay
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase()
    return notifs.filter(n => {
      if (statusFilter !== 'all' && n.status !== statusFilter) return false
      if (!q) return true
      return [n.no, n.title, n.audience, n.raw?.message]
        .some(v => String(v ?? '').toLowerCase().includes(q))
    })
  }, [notifs, statusFilter, query])

  const filtering = statusFilter !== 'all' || query.trim() !== ''

  /* ---------- pagination ---------- */
  useEffect(() => { setPage(1) }, [statusFilter, query])

  const totalPages = Math.max(1, Math.ceil(visible.length / pageSize))
  const currentPage = Math.min(page, totalPages)
  const paginated = visible.slice((currentPage - 1) * pageSize, currentPage * pageSize)

  const handlePageSizeChange = (size) => { setPageSize(size); setPage(1) }

  const stats = [
    { label: 'Total Sent', value: sentCount, variant: 'indigo', icon: Sparkles, color: '#4F46E5', colorGlow: 'rgba(79,70,229,0.18)', bar: pct(sentCount, total), trendColor: '#4F46E5' },
    { label: 'Scheduled', value: scheduledCount, variant: 'amber', icon: Clock4, color: '#F59E0B', colorGlow: 'rgba(245,158,11,0.15)', bar: pct(scheduledCount, total), trendDir: 'neutral', trendColor: '#F59E0B' },
    { label: 'Drafts', value: draftCount, variant: 'cyan', icon: Pencil, color: '#06B6D4', colorGlow: 'rgba(6,182,212,0.15)', bar: pct(draftCount, total), trendDir: 'neutral', trendColor: '#06B6D4' },
    { label: 'Total Campaigns', value: total, variant: 'green', icon: LayoutList, color: '#10B981', colorGlow: 'rgba(16,185,129,0.15)', bar: total ? 100 : 0, trendColor: '#10B981' },
  ]

  const autoReach = AUTO_REACH.includes(form.audience)
  const primaryLabel = active
    ? 'Update'
    : form.status === 'sent' ? 'Send Now' : form.status === 'scheduled' ? 'Schedule' : 'Save'

  return (
    <div className="dash-page page-in">
      <section className="notif-hero card">
        <div className="notif-hero-glow" />
        <div>
          <p className="notif-overline">Campaign Center</p>
          <h1 className="page-title" style={{ marginBottom: 4 }}>Push Notifications</h1>
          <p style={{ color: 'var(--text3)', fontSize: 'var(--fs-sm)' }}>Create and monitor targeted broadcast campaigns.</p>
        </div>
        <button className="btn btn-primary" onClick={openAdd}><Plus size={14} /> New Notification</button>
      </section>

      <div className="stats-grid cols-4">
        {stats.map(s => (
          <div key={s.label} className={`stat-card stat-card--${s.variant}`}>
            <span style={{ position: 'absolute', top: 0, right: 0, width: 110, height: 110, borderRadius: '50%', background: s.colorGlow, filter: 'blur(30px)', transform: 'translate(30%, -30%)', pointerEvents: 'none' }} />
            <div className="stat-card-header">
              <div className="stat-card-icon" style={{ background: s.colorGlow, color: s.color, border: `1px solid ${s.colorGlow}` }}>
                <s.icon size={15} strokeWidth={1.8} />
              </div>
              <span className={`stat-card-trend${s.trendDir === 'neutral' ? ' neutral' : ''}`} style={{ color: s.trendColor, background: `${s.trendColor}15`, border: `1px solid ${s.colorGlow}` }}>
                {s.bar}%
              </span>
            </div>
            <div className="stat-card-value">{s.value}</div>
            <div className="stat-card-label">{s.label}</div>
            <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 2, background: `linear-gradient(90deg,${s.color}88,transparent)`, borderRadius: '0 0 var(--radius-lg) var(--radius-lg)' }} />
          </div>
        ))}
      </div>

      <div className="card" style={{ padding: 0 }}>
        <div className="nf-toolbar">
          <label className="nf-search">
            <Search size={15} aria-hidden="true" />
            <input
              type="search"
              placeholder="Search title, audience or ID..."
              aria-label="Search notifications"
              value={query}
              onChange={e => setQuery(e.target.value)}
            />
          </label>

          <div className="nf-tabs" role="tablist" aria-label="Filter by status">
            {FILTER_TABS.map(t => (
              <button
                key={t.key}
                type="button"
                role="tab"
                aria-selected={statusFilter === t.key}
                className={`nf-tab${statusFilter === t.key ? ' active' : ''}`}
                onClick={() => setStatusFilter(t.key)}
              >
                {t.label}
                <span className="nf-count">{tabCounts[t.key]}</span>
              </button>
            ))}
          </div>

          <button className="btn btn-outline btn-sm" onClick={load}>Refresh</button>
        </div>

        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr><th>ID</th><th>Title</th><th>Audience</th><th>Reach</th><th>Status</th><th>Date</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={7} style={{ textAlign: 'center', padding: 28, color: 'var(--text3)' }}>Loading notifications…</td></tr>
              )}
              {!loading && loadError && (
                <tr><td colSpan={7} style={{ textAlign: 'center', padding: 28, color: '#EF4444' }}>{loadError}</td></tr>
              )}
              {!loading && !loadError && notifs.length === 0 && (
                <tr><td colSpan={7} style={{ textAlign: 'center', padding: 28, color: 'var(--text3)' }}>No notifications yet</td></tr>
              )}
              {!loading && !loadError && notifs.length > 0 && visible.length === 0 && (
                <tr>
                  <td colSpan={7} style={{ textAlign: 'center', padding: 28, color: 'var(--text3)' }}>
                    No notifications match your filter.{' '}
                    {filtering && (
                      <button type="button" className="nf-clear" onClick={() => { setStatusFilter('all'); setQuery('') }}>Clear filters</button>
                    )}
                  </td>
                </tr>
              )}
              {!loading && paginated.map(n => (
                <tr key={n.id}>
                  <td style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)', color: 'var(--accent-hover)' }}>{n.no}</td>
                  <td style={{ fontWeight: 500, color: 'var(--text1)' }}>{n.title}</td>
                  <td>{n.audience}</td>
                  <td style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)' }}>{n.reach}</td>
                  <td><span className={`badge ${STATUS[n.status]?.cls}`}>{STATUS[n.status]?.l}</span></td>
                  <td style={{ color: 'var(--text3)', fontSize: 'var(--fs-xs)' }}>{n.sent}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn btn-ghost btn-icon" onClick={() => openEdit(n)} aria-label="Edit"><Pencil size={14} /></button>
                      <button className="btn btn-ghost btn-icon" onClick={() => { setActive(n); setDeleteOpen(true) }} aria-label="Delete"><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {!loading && !loadError && visible.length > 0 && (
          <Pagination
            total={visible.length}
            page={currentPage}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={handlePageSizeChange}
            pageSizeOptions={[10, 20, 50, 100]}
          />
        )}
      </div>

      <Modal
        open={modalOpen}
        title={active ? 'Edit Notification' : 'Compose Notification'}
        subtitle="Create campaign and target audience"
        onClose={() => setModalOpen(false)}
        footer={(
          <>
            <button className="btn btn-outline" onClick={() => setModalOpen(false)} disabled={saving}>Cancel</button>
            <button className="btn btn-ghost" onClick={() => save('draft')} disabled={saving}>Save as Draft</button>
            <button className="btn btn-primary" onClick={() => save()} disabled={saving}>
              <Send size={14} /> {saving ? 'Saving…' : primaryLabel}
            </button>
          </>
        )}
      >
        {formError && (
          <div style={{ marginBottom: 12, padding: '9px 12px', borderRadius: 8, fontSize: 12.5, fontWeight: 600, color: '#EF4444', background: 'rgba(239,68,68,.10)', border: '1px solid rgba(239,68,68,.30)' }}>
            {formError}
          </div>
        )}
        <div className="form-grid cols-2">
          <div className="form-field full">
            <label>Notification Title</label>
            <input
              className="input"
              placeholder="Enter title"
              maxLength={150}
              value={form.title}
              onChange={e => setForm({ ...form, title: e.target.value })}
            />
          </div>

          <div className="form-field full">
            <label>Target Audience</label>
            <AudienceSelect value={form.audience} onChange={v => setForm({ ...form, audience: v })} />
          </div>

          <div className="form-field">
            <label>Campaign Status</label>
            <Select
              value={form.status}
              onChange={v => setForm({ ...form, status: v })}
              options={STATUS_OPTIONS}
              ariaLabel="Campaign status"
            />
          </div>

          <div className="form-field">
            <label>Expected Reach {autoReach && '(auto)'}</label>
            <input
              className="input"
              placeholder={autoReach ? 'Auto calculated' : 'e.g. 24000'}
              disabled={autoReach}
              value={autoReach ? '' : form.reach}
              onChange={e => setForm({ ...form, reach: e.target.value })}
            />
          </div>

          {form.status === 'scheduled' && (
            <div className="form-field full">
              <label>Schedule Date &amp; Time</label>
              <input className="input" type="datetime-local" value={form.scheduled_at} onChange={e => setForm({ ...form, scheduled_at: e.target.value })} />
            </div>
          )}

          <div className="form-field full">
            <label>Message <span style={{ color: 'var(--text3)', fontWeight: 400 }}>({form.message.length}/500)</span></label>
            <textarea className="input" rows={5} maxLength={500} placeholder="Write campaign message..." value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} style={{ resize: 'vertical' }} />
          </div>
        </div>
      </Modal>

      <ConfirmModal
        open={deleteOpen}
        title="Delete notification"
        message={`Delete ${active?.title || 'selected notification'}?`}
        onClose={() => setDeleteOpen(false)}
        onConfirm={onDelete}
      />

      <style>{`
        .dash-page { display:flex; flex-direction:column; gap:var(--space-lg); width:100%; }
        .notif-hero {
          position:relative; display:flex; justify-content:space-between;
          align-items:flex-end; gap:var(--space-md); flex-wrap:wrap;
          background: radial-gradient(120% 150% at 0% 0%, rgba(79,70,229,.22), rgba(6,182,212,.08));
          border-color: var(--border-active);
        }
        .notif-overline { font-size:var(--fs-xs); text-transform:uppercase; letter-spacing:.08em; color:var(--accent-hover); font-weight:700; margin-bottom:4px; }
        .notif-hero-glow { position:absolute; width:220px; height:220px; right:-70px; top:-70px; border-radius:50%; background:rgba(6,182,212,.16); filter:blur(40px); pointer-events:none; }

        /* ---------- Search + status tabs ---------- */
        .nf-toolbar {
          display:flex; align-items:center; gap:var(--space-md); flex-wrap:wrap;
          padding:var(--space-md) var(--space-lg);
          border-bottom:1px solid var(--border);
        }
        .nf-search {
          flex:1 1 260px; min-width:0; display:flex; align-items:center; gap:10px;
          padding:0 12px; height:38px; border-radius:10px; cursor:text;
          background: #f6f8ff; color:var(--text3);
          border:1px solid var(--border); transition:border-color .18s ease, box-shadow .18s ease;
        }
        .nf-search:focus-within { border-color:var(--accent); box-shadow:0 0 0 3px rgba(79,70,229,.16); }
        .nf-search input {
          flex:1; min-width:0; border:0; outline:0; background:transparent;
          font:inherit; font-size:var(--fs-sm); color:var(--text1);
        }
        .nf-search input::placeholder { color:var(--text3); }

        .nf-tabs {
          display:flex; align-items:center; gap:4px; padding:4px;
          border:1px solid var(--border); border-radius:12px;
        }
        .nf-tab {
          display:inline-flex; align-items:center; gap:8px; height:30px; padding:0 12px;
          border:1px solid transparent; border-radius:9px; cursor:pointer;
          background:transparent; color:var(--text3);
          font:inherit; font-size:var(--fs-xs); font-weight:600; white-space:nowrap;
          transition:background .15s ease, color .15s ease, border-color .15s ease;
        }
        .nf-tab:hover { color:var(--text1); }
        .nf-tab:focus-visible { outline:2px solid var(--accent); outline-offset:1px; }
        .nf-tab.active {
          background: #f6f8ff; border-color:var(--border-active); color:var(--text1);
        }
        .nf-count {
          min-width:20px; padding:1px 6px; border-radius:999px; text-align:center;
          font-size:10.5px; font-weight:700; line-height:16px;
          background:rgba(148,163,184,.22); color:var(--text3);
        }
        .nf-tab.active .nf-count { background:var(--accent); color:#fff; }

        .nf-clear {
          border:0; background:transparent; cursor:pointer; padding:0;
          font:inherit; font-weight:600; color:var(--accent-hover); text-decoration:underline;
        }

        @media (max-width:760px) {
          .nf-tabs { width:100%; overflow-x:auto; }
          .nf-tab { flex:1 0 auto; justify-content:center; }
        }

        /* ---------- Audience dropdown ---------- */
        .aud-select { position:relative; }
        .aud-trigger {
          width:100%; display:flex; align-items:center; gap:12px; text-align:left;
          padding:10px 12px; border-radius:12px; cursor:pointer;
          background:transparent; color:var(--text1);
          border:1px solid var(--border); transition:all .18s ease;
        }
        .aud-trigger:hover { border-color:var(--border-active); }
        .aud-trigger.open { border-color:var(--accent); box-shadow:0 0 0 3px rgba(79,70,229,.16); }
        .aud-trigger .aud-txt { flex:1; }
        .aud-chev { color:var(--text3); transition:transform .2s ease; }
        .aud-trigger.open .aud-chev { transform:rotate(180deg); }

        .aud-menu {
          position:absolute; z-index:30; left:0; right:0; top:calc(100% + 6px);
          margin:0; padding:6px; list-style:none;
          background:var(--bg-card, var(--bg2, #14142b)); border:1px solid var(--border-active);
          border-radius:14px; box-shadow:0 18px 40px rgba(0,0,0,.35);
          animation:audPop .16s ease;
        }
        @keyframes audPop { from { opacity:0; transform:translateY(-6px); } to { opacity:1; transform:none; } }
        .aud-opt {
          width:100%; display:flex; align-items:center; gap:12px; text-align:left;
          padding:9px 10px; border-radius:10px; cursor:pointer;
          background:transparent; border:0; color:var(--text1); transition:background .15s ease;
        }
        .aud-opt:hover { background:rgba(79,70,229,.10); }
        .aud-opt.active { background:rgba(79,70,229,.16); }
        .aud-opt .aud-txt { flex:1; }
        .aud-tick { color:var(--accent-hover); }

        .aud-ico {
          flex:0 0 32px; width:32px; height:32px; border-radius:9px;
          display:flex; align-items:center; justify-content:center;
          background:rgba(79,70,229,.14); color:var(--accent-hover);
        }
        .aud-opt.active .aud-ico { background:var(--accent); color:#fff; }
        .aud-txt { display:flex; flex-direction:column; min-width:0; }
        .aud-txt b { font-size:var(--fs-sm); font-weight:600; color:var(--text1); }
        .aud-txt small { font-size:var(--fs-xs); color:var(--text3); }
      `}</style>
    </div>
  )
}